import React from "react";
import { CreateFunction, UpdateFunction } from "@hex-insights/app-modules";
import { ClassNameProps, Column, DistributiveOmit, StyleProps } from "@hex-insights/core";
import { FieldIf, FieldSet, Form, FormEditButtonPair, FormState, SubmitButton } from "@hex-insights/forms";
import {
	StudentInjuryNoteCreateMutation,
	StudentInjuryNoteDetailQuery,
	StudentInjuryNoteUpdateMutation,
} from "../../../Utilities";
import { StudentInjuryNoteFormat } from "../../Format";
import { StudentInjuryNoteFormState, StudentInjuryNoteFormValues } from "../../FormUtilities";
import { BaseFormProps } from "../shared";
import * as Field from "./Field";
import {
	useCreateFormControl,
	useCreateFormSubmission,
	useDetailFormControl,
	useDetailFormSubmission,
	useReadOnlyForm,
} from "./hooks";

export {
	type UseCreateFormArgs,
	useCreateForm,
	type UseDetailFormArgs,
	useDetailForm,
	type UseReadOnlyFormArgs,
	useReadOnlyForm,
} from "./hooks";

export type ControlledCreateProps = Omit<
	CreateProps<StudentInjuryNoteCreateMutation["createStudentInjuryNote"]>,
	"formState" | "create"
> & {
	formStateArgs?: StudentInjuryNoteFormState.UseCreateFormStateArgs;
};

/**
 * Create form of the StudentInjuryNote model using an internally managed form state.
 */
export function ControlledCreate({ formStateArgs, ...props }: ControlledCreateProps) {
	const { formState, create } = useCreateFormControl(formStateArgs);

	return <Create formState={formState} create={create} {...props} />;
}

export type CreateProps<T> = {
	formState: FormState<StudentInjuryNoteFormValues.Create>;
	create: CreateFunction<StudentInjuryNoteFormValues.Create, T>;
	onSuccess: (formState: FormState<StudentInjuryNoteFormValues.Create>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<StudentInjuryNoteFormValues.Create> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Create form of the StudentInjuryNote model using the given form state.
 */
export function Create<T>({ formState, create, onSuccess, formNameSuffix, ...props }: CreateProps<T>) {
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	const formName = "studentInjuryNote.create" + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<CreateFields formState={formState} />

				<Form.ActionBar>
					<SubmitButton submissionStatus={formState.submissionStatus} onClick={formState.onSubmitWrapper(onSubmit)}>
						Submit
					</SubmitButton>
				</Form.ActionBar>
			</Form.Column>
		</Form>
	);
}

type CreateFieldsProps = {
	formState: FormState<StudentInjuryNoteFormValues.Create>;
};

function CreateFields({ formState }: CreateFieldsProps) {
	return (
		<React.Fragment>
			<Field.IncidentTime formState={formState} />
			<Field.IncidentLocation formState={formState} />
			<Field.WereOtherStudentsCauseOfInjury formState={formState} />
			<Field.CauseOfInjury formState={formState} />
			<Field.MedicalAttentionProvided formState={formState} />
			<Field.AdultWhoProvidedMedicalAttention formState={formState} />
			<Field.IsActionTakenHomeroomTeachersNotified formState={formState} />
			<Field.IsActionTakenParentNotifiedByPortal formState={formState} />
			<Field.IsActionTakenParentNotifiedByPhone formState={formState} />
			<Field.IsActionTakenParentsRequestedToCollectStudent formState={formState} />
			<Field.IsActionTakenParentNotificationNotNecessary formState={formState} />
			<Field.IsActionTakenOther formState={formState} />
			<Field.ActionTakenOther formState={formState} />
			<Field.StudentNote.Select formState={formState} />
		</React.Fragment>
	);
}

export type ControlledDetailProps = Omit<
	DetailProps<StudentInjuryNoteUpdateMutation["updateStudentInjuryNote"]>,
	"formState" | "update"
> & {
	formStateArgs?: Partial<DistributiveOmit<StudentInjuryNoteFormState.UseDetailFormStateArgs, "initialFormValues">>;
};

/**
 * Detail form of the StudentInjuryNote model using an internally managed form state.
 */
export function ControlledDetail({ formStateArgs, ...props }: ControlledDetailProps) {
	const { formState, update } = useDetailFormControl({ ...formStateArgs, studentInjuryNote: props.studentInjuryNote });

	return <Detail formState={formState} update={update} {...props} />;
}

export type DetailProps<T> = {
	studentInjuryNote: StudentInjuryNoteDetailQuery["studentInjuryNote"];
	formState: FormState<StudentInjuryNoteFormValues.Detail>;
	update: UpdateFunction<StudentInjuryNoteFormValues.Detail, T>;
	onSuccess: (formState: FormState<StudentInjuryNoteFormValues.Detail>, data: T) => void;
	formNameSuffix?: string;
} & BaseFormProps<StudentInjuryNoteFormValues.Detail> &
	Partial<ClassNameProps & StyleProps>;

/**
 * Detail form of the StudentInjuryNote model using the given form state.
 */
export function Detail<T>({
	formState,
	studentInjuryNote,
	update,
	onSuccess,
	formNameSuffix,
	...props
}: DetailProps<T>) {
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	const formName = `studentInjuryNote.detail.${studentInjuryNote.id}` + (formNameSuffix ? "." + formNameSuffix : "");

	return (
		<Form name={formName} formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} {...props}>
			<Form.Column>
				<FormEditButtonPair formState={formState} />

				<DetailFields formState={formState} studentInjuryNote={studentInjuryNote} />
			</Form.Column>
		</Form>
	);
}

export type ControlledReadOnlyProps = Omit<ReadOnlyProps, "formState"> & {
	formStateArgs?: Partial<DistributiveOmit<StudentInjuryNoteFormState.UseReadOnlyFormStateArgs, "initialFormValues">>;
};

/**
 * Read-only detail form of the StudentInjuryNote model using an internally managed form state.
 */
export function ControlledReadOnly({ formStateArgs, ...props }: ControlledReadOnlyProps) {
	const { formState } = useReadOnlyForm({ ...formStateArgs, studentInjuryNote: props.studentInjuryNote });

	return <ReadOnly formState={formState} {...props} />;
}

export type ReadOnlyProps = {
	studentInjuryNote: StudentInjuryNoteDetailQuery["studentInjuryNote"];
	formState: FormState<StudentInjuryNoteFormValues.Detail>;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Read-only detail form of the StudentInjuryNote model using the given form state.
 */
export function ReadOnly({ formState, studentInjuryNote, className, style }: ReadOnlyProps) {
	return (
		<div className={className} style={style}>
			<Form.Column>
				<DetailFields formState={formState} studentInjuryNote={studentInjuryNote} />
			</Form.Column>
		</div>
	);
}

type DetailFieldsProps = {
	studentInjuryNote: StudentInjuryNoteDetailQuery["studentInjuryNote"];
	formState: FormState<StudentInjuryNoteFormValues.Detail>;
};

function DetailFields({ formState, studentInjuryNote }: DetailFieldsProps) {
	return (
		<React.Fragment>
			<Field.IncidentTime formState={formState} />
			<Field.IncidentLocation formState={formState} />
			<Field.WereOtherStudentsCauseOfInjury formState={formState} />
			<Field.CauseOfInjury formState={formState} />
			<Field.MedicalAttentionProvided formState={formState} />
			<Field.AdultWhoProvidedMedicalAttention formState={formState} />
			<Field.IsActionTakenHomeroomTeachersNotified formState={formState} />
			<Field.IsActionTakenParentNotifiedByPortal formState={formState} />
			<Field.IsActionTakenParentNotifiedByPhone formState={formState} />
			<Field.IsActionTakenParentsRequestedToCollectStudent formState={formState} />
			<Field.IsActionTakenParentNotificationNotNecessary formState={formState} />
			<Field.IsActionTakenOther formState={formState} />
			<Field.ActionTakenOther formState={formState} />
			<Field.StudentNote.Select formState={formState} currentInstance={studentInjuryNote.studentNote} />
		</React.Fragment>
	);
}

const actionTakenFieldNames: (keyof Omit<StudentInjuryNoteFormValues.CreateInStudentNote, "studentNoteID">)[] = [
	"isActionTakenHomeroomTeachersNotified",
	"isActionTakenParentNotifiedByPortal",
	"isActionTakenParentNotifiedByPhone",
	"isActionTakenParentsRequestedToCollectStudent",
	"isActionTakenParentNotificationNotNecessary",
	"isActionTakenOther",
	"actionTakenOther",
];

export type CreateFieldsInStudentNoteProps = {
	formState: FormState<StudentInjuryNoteFormValues.CreateInStudentNote>;
};

export function CreateFieldsInStudentNote({ formState }: CreateFieldsInStudentNoteProps) {
	return (
		<React.Fragment>
			<Field.IncidentTime formState={formState} />
			<Field.IncidentLocation formState={formState} />
			<Field.WereOtherStudentsCauseOfInjury formState={formState} />
			<Field.CauseOfInjury formState={formState} style={{ width: "100%" }} />
			<Field.MedicalAttentionProvided formState={formState} style={{ width: "100%" }} />
			<Field.AdultWhoProvidedMedicalAttention formState={formState} style={{ width: "100%" }} />
			<FieldSet
				formState={formState}
				names={actionTakenFieldNames}
				label={StudentInjuryNoteFormat.fieldSetLabels.actionTaken}
				style={{ width: "100%" }}
			>
				<Column
					justify="spaced-start"
					verticalSpacing="0.25rem"
					style={{ padding: "var(--radio-field__value---padding)" }}
				>
					<Field.IsActionTakenHomeroomTeachersNotified formState={formState} style={{ width: "100%" }} />
					<Field.IsActionTakenParentNotifiedByPortal formState={formState} style={{ width: "100%" }} />
					<Field.IsActionTakenParentNotifiedByPhone formState={formState} style={{ width: "100%" }} />
					<Field.IsActionTakenParentsRequestedToCollectStudent formState={formState} style={{ width: "100%" }} />
					<Field.IsActionTakenParentNotificationNotNecessary formState={formState} style={{ width: "100%" }} />
					<Field.IsActionTakenOther formState={formState} style={{ width: "100%" }} />
					<FieldIf formState={formState} name="actionTakenOther" condition={formState.formValues.isActionTakenOther}>
						<Field.ActionTakenOther formState={formState} style={{ width: "100%" }} />
					</FieldIf>
				</Column>
			</FieldSet>
		</React.Fragment>
	);
}
